// Room types
export const ROOM_TYPES = {
  ROOM: 'room',
  HALL: 'hall',
  OUTDOOR_SPACE: 'outdoor_space',
  GARDEN: 'garden',
  TERRACE: 'terrace',
  PAVILION: 'pavilion',
  TENT: 'tent',
  BALLROOM: 'ballroom',
  CONFERENCE_ROOM: 'conference_room',
  CHAPEL: 'chapel',
  OTHER: 'other'
} as const;

export type RoomType = typeof ROOM_TYPES[keyof typeof ROOM_TYPES];

// Seating configuration types
export const SEATING_CONFIG_TYPES = {
  THEATER: 'theater',
  CLASSROOM: 'classroom',
  U_SHAPE: 'u_shape',
  BOARDROOM: 'boardroom',
  BANQUET: 'banquet',
  CABARET: 'cabaret',
  HOLLOW_SQUARE: 'hollow_square',
  COCKTAIL_RECEPTION: 'cocktail_reception',
  CLUSTER_POD: 'cluster_pod',
  CHEVRON_V_SHAPE: 'chevron_v_shape',
  FISHBONE_HERRINGBONE: 'fishbone_herringbone',
  LOUNGE: 'lounge',
  AMPHITHEATER: 'amphitheater',
  HYBRID_VIRTUAL: 'hybrid_virtual',
  ECO_FRIENDLY_OPEN_AIR: 'eco_friendly_open_air',
  THEMED_CUSTOM_MODULAR: 'themed_custom_modular'
} as const;

export type SeatingConfigType = typeof SEATING_CONFIG_TYPES[keyof typeof SEATING_CONFIG_TYPES];

// Labels for display
export const SEATING_CONFIG_LABELS: Record<SeatingConfigType, string> = {
  theater: 'Theater',
  classroom: 'Classroom',
  u_shape: 'U-Shape',
  boardroom: 'Boardroom',
  banquet: 'Banquet',
  cabaret: 'Cabaret',
  hollow_square: 'Hollow Square',
  cocktail_reception: 'Cocktail / Reception',
  cluster_pod: 'Cluster / Pod',
  chevron_v_shape: 'Chevron / V-Shape',
  fishbone_herringbone: 'Fishbone / Herringbone',
  lounge: 'Lounge',
  amphitheater: 'Amphitheater',
  hybrid_virtual: 'Hybrid / Virtual',
  eco_friendly_open_air: 'Eco-Friendly / Open-Air',
  themed_custom_modular: 'Themed / Custom Modular'
};

export const SEATING_CONFIG_DESCRIPTIONS: Record<SeatingConfigType, string> = {
  theater: 'Rows of chairs facing the stage or presentation area',
  classroom: 'Rows of tables and chairs, all facing the presenter',
  u_shape: 'Tables arranged in a U shape, facing inward',
  boardroom: 'One large table with chairs around it',
  banquet: 'Round tables with chairs, for dining events',
  cabaret: 'Round tables with chairs on one side only, facing stage',
  hollow_square: 'Tables arranged in a square with an open center',
  cocktail_reception: 'Standing room with scattered high-top tables',
  cluster_pod: 'Small groups of tables, for workshops and collaboration',
  chevron_v_shape: 'Angled rows like a V, facing the presenter',
  fishbone_herringbone: 'Tables angled in a herringbone pattern',
  lounge: 'Comfortable seating with sofas and armchairs',
  amphitheater: 'Tiered/stepped seating in a semicircle',
  hybrid_virtual: 'Mixed in-person and virtual attendee setup',
  eco_friendly_open_air: 'Outdoor seating using natural elements',
  themed_custom_modular: 'Custom modular layout for themed events'
};

// Suggested seating configurations by room type
// Used for "Quick Add" feature to pre-populate common layouts
export const SEATING_CONFIG_SUGGESTIONS: Record<RoomType, SeatingConfigType[]> = {
  ballroom: ['banquet', 'theater', 'cocktail_reception', 'cabaret'],
  conference_room: ['boardroom', 'u_shape', 'classroom', 'theater'],
  hall: ['theater', 'banquet', 'classroom', 'cocktail_reception'],
  outdoor_space: ['cocktail_reception', 'cluster_pod', 'eco_friendly_open_air', 'lounge'],
  chapel: ['theater', 'amphitheater'],
  garden: ['cocktail_reception', 'eco_friendly_open_air', 'lounge'],
  terrace: ['cocktail_reception', 'lounge', 'banquet'],
  pavilion: ['banquet', 'cocktail_reception', 'theater'],
  tent: ['banquet', 'theater', 'cocktail_reception'],
  room: ['boardroom', 'u_shape', 'classroom'],
  other: ['theater', 'banquet', 'cocktail_reception']
};

export const ROOM_TYPE_LABELS: Record<RoomType, string> = {
  room: 'Room',
  hall: 'Hall',
  outdoor_space: 'Outdoor Space',
  garden: 'Garden',
  terrace: 'Terrace',
  pavilion: 'Pavilion',
  tent: 'Tent',
  ballroom: 'Ballroom',
  conference_room: 'Conference Room',
  chapel: 'Chapel',
  other: 'Other'
};

// ---- Seating Parameter Definitions ----

/** Definition of a single parameter for a seating configuration type */
export interface SeatingParamDefinition {
  key: string;
  label: string;
  min: number;
  max: number;
  placeholder: string;
}

/** Parameter definitions for each seating configuration type */
export const SEATING_CONFIG_PARAMS: Record<SeatingConfigType, SeatingParamDefinition[]> = {
  theater: [
    { key: 'rows', label: 'Number of Rows', min: 1, max: 200, placeholder: 'e.g. 20' },
    { key: 'seats_per_row', label: 'Seats per Row', min: 1, max: 100, placeholder: 'e.g. 15' },
    { key: 'aisles', label: 'Number of Aisles', min: 0, max: 10, placeholder: 'e.g. 2' },
  ],
  classroom: [
    { key: 'rows', label: 'Number of Rows', min: 1, max: 100, placeholder: 'e.g. 10' },
    { key: 'seats_per_row', label: 'Seats per Row', min: 1, max: 20, placeholder: 'e.g. 6' },
    { key: 'blocks', label: 'Number of Blocks/Columns', min: 1, max: 10, placeholder: 'e.g. 2' },
  ],
  u_shape: [
    { key: 'tables_per_side', label: 'Tables per Side', min: 1, max: 20, placeholder: 'e.g. 4' },
    { key: 'seats_per_table', label: 'Seats per Table', min: 1, max: 10, placeholder: 'e.g. 3' },
    { key: 'head_table_seats', label: 'Head Table Seats', min: 0, max: 20, placeholder: 'e.g. 4' },
  ],
  boardroom: [
    { key: 'total_seats', label: 'Total Seats', min: 1, max: 60, placeholder: 'e.g. 20' },
  ],
  banquet: [
    { key: 'tables', label: 'Number of Tables', min: 1, max: 200, placeholder: 'e.g. 10' },
    { key: 'seats_per_table', label: 'Seats per Table', min: 1, max: 20, placeholder: 'e.g. 8' },
  ],
  cabaret: [
    { key: 'tables', label: 'Number of Tables', min: 1, max: 200, placeholder: 'e.g. 12' },
    { key: 'seats_per_table', label: 'Seats per Table (one side)', min: 1, max: 10, placeholder: 'e.g. 5' },
  ],
  hollow_square: [
    { key: 'tables_per_side', label: 'Tables per Side', min: 1, max: 20, placeholder: 'e.g. 4' },
    { key: 'seats_per_side', label: 'Seats per Side', min: 1, max: 30, placeholder: 'e.g. 8' },
  ],
  cocktail_reception: [
    { key: 'high_top_tables', label: 'High-Top Tables', min: 0, max: 100, placeholder: 'e.g. 10' },
    { key: 'seats_per_high_top', label: 'Seats per High-Top', min: 0, max: 8, placeholder: 'e.g. 4' },
    { key: 'standing_capacity', label: 'Standing Capacity', min: 0, max: 2000, placeholder: 'e.g. 100' },
  ],
  cluster_pod: [
    { key: 'clusters', label: 'Number of Clusters', min: 1, max: 100, placeholder: 'e.g. 8' },
    { key: 'seats_per_cluster', label: 'Seats per Cluster', min: 1, max: 12, placeholder: 'e.g. 6' },
  ],
  chevron_v_shape: [
    { key: 'rows', label: 'Number of Rows', min: 1, max: 100, placeholder: 'e.g. 10' },
    { key: 'seats_per_row', label: 'Seats per Row', min: 1, max: 50, placeholder: 'e.g. 12' },
    { key: 'sections', label: 'Number of Sections', min: 1, max: 6, placeholder: 'e.g. 2' },
  ],
  fishbone_herringbone: [
    { key: 'tables', label: 'Number of Tables', min: 1, max: 100, placeholder: 'e.g. 10' },
    { key: 'seats_per_table', label: 'Seats per Table', min: 1, max: 12, placeholder: 'e.g. 6' },
  ],
  lounge: [
    { key: 'seating_groups', label: 'Seating Groups', min: 1, max: 50, placeholder: 'e.g. 6' },
    { key: 'seats_per_group', label: 'Seats per Group', min: 1, max: 12, placeholder: 'e.g. 4' },
  ],
  amphitheater: [
    { key: 'tiers', label: 'Number of Tiers', min: 1, max: 50, placeholder: 'e.g. 8' },
    { key: 'seats_per_tier', label: 'Seats per Tier', min: 1, max: 100, placeholder: 'e.g. 20' },
  ],
  hybrid_virtual: [
    { key: 'in_person_seats', label: 'In-Person Seats', min: 0, max: 1000, placeholder: 'e.g. 50' },
    { key: 'virtual_stations', label: 'Virtual Stations', min: 0, max: 500, placeholder: 'e.g. 100' },
  ],
  eco_friendly_open_air: [
    { key: 'seating_areas', label: 'Seating Areas', min: 1, max: 50, placeholder: 'e.g. 5' },
    { key: 'seats_per_area', label: 'Seats per Area', min: 1, max: 50, placeholder: 'e.g. 10' },
  ],
  themed_custom_modular: [
    { key: 'modules', label: 'Number of Modules', min: 1, max: 100, placeholder: 'e.g. 8' },
    { key: 'seats_per_module', label: 'Seats per Module', min: 1, max: 20, placeholder: 'e.g. 6' },
  ],
};

/**
 * Suggest seating parameters based on guest count.
 * Uses industry-standard defaults (e.g. 6 seats per banquet table).
 * Returns a full params object ready to use.
 */
export function suggestSeatingParams(
  configType: SeatingConfigType,
  guestCount: number
): Record<string, number> {
  const g = Math.max(1, guestCount);

  switch (configType) {
    case 'banquet': {
      const seatsPerTable = 6;
      const tables = Math.ceil(g / seatsPerTable);
      return { tables, seats_per_table: seatsPerTable };
    }
    case 'cabaret': {
      const seatsPerTable = 5;
      const tables = Math.ceil(g / seatsPerTable);
      return { tables, seats_per_table: seatsPerTable };
    }
    case 'theater': {
      const seatsPerRow = Math.min(20, Math.max(8, Math.round(Math.sqrt(g * 1.5))));
      const rows = Math.ceil(g / seatsPerRow);
      const aisles = seatsPerRow >= 15 ? 2 : seatsPerRow >= 10 ? 1 : 0;
      return { rows, seats_per_row: seatsPerRow, aisles };
    }
    case 'classroom': {
      const seatsPerRow = 6;
      const blocks = 2;
      const rows = Math.ceil(g / seatsPerRow);
      return { rows, seats_per_row: seatsPerRow, blocks };
    }
    case 'u_shape': {
      const seatsPerTable = 3;
      const headTableSeats = 4;
      const tablesPerSide = Math.max(1, Math.ceil((g - headTableSeats) / (2 * seatsPerTable)));
      return { tables_per_side: tablesPerSide, seats_per_table: seatsPerTable, head_table_seats: headTableSeats };
    }
    case 'boardroom': {
      return { total_seats: g };
    }
    case 'hollow_square': {
      const seatsPerSide = Math.max(2, Math.ceil(g / 4));
      return { tables_per_side: Math.max(1, Math.ceil(seatsPerSide / 3)), seats_per_side: seatsPerSide };
    }
    case 'cocktail_reception': {
      const seatsPerHighTop = 4;
      const highTopTables = Math.round(g * 0.6 / seatsPerHighTop);
      const standingCapacity = Math.ceil(g * 0.4);
      return { high_top_tables: highTopTables, seats_per_high_top: seatsPerHighTop, standing_capacity: standingCapacity };
    }
    case 'cluster_pod': {
      const seatsPerCluster = 6;
      const clusters = Math.ceil(g / seatsPerCluster);
      return { clusters, seats_per_cluster: seatsPerCluster };
    }
    case 'chevron_v_shape': {
      const seatsPerRow = 12;
      const sections = 2;
      const rows = Math.ceil(g / seatsPerRow);
      return { rows, seats_per_row: seatsPerRow, sections };
    }
    case 'fishbone_herringbone': {
      const seatsPerTable = 6;
      const tables = Math.ceil(g / seatsPerTable);
      return { tables, seats_per_table: seatsPerTable };
    }
    case 'lounge': {
      const seatsPerGroup = 4;
      const seatingGroups = Math.ceil(g / seatsPerGroup);
      return { seating_groups: seatingGroups, seats_per_group: seatsPerGroup };
    }
    case 'amphitheater': {
      const seatsPerTier = Math.min(40, Math.max(8, Math.round(Math.sqrt(g * 2))));
      const tiers = Math.ceil(g / seatsPerTier);
      return { tiers, seats_per_tier: seatsPerTier };
    }
    case 'hybrid_virtual': {
      const inPerson = Math.ceil(g * 0.5);
      const virtual = g - inPerson;
      return { in_person_seats: inPerson, virtual_stations: virtual };
    }
    case 'eco_friendly_open_air': {
      const seatsPerArea = 8;
      const seatingAreas = Math.ceil(g / seatsPerArea);
      return { seating_areas: seatingAreas, seats_per_area: seatsPerArea };
    }
    case 'themed_custom_modular': {
      const seatsPerModule = 6;
      const modules = Math.ceil(g / seatsPerModule);
      return { modules, seats_per_module: seatsPerModule };
    }
    default:
      return {};
  }
}

/**
 * Calculate total seating capacity from config type and parameter values.
 * Returns null if required parameters are missing.
 */
export function calculateSeatingCapacity(
  configType: SeatingConfigType,
  params: Record<string, number>
): number | null {
  const p = (key: string): number => params[key] ?? 0;

  switch (configType) {
    case 'theater':           return p('rows') * p('seats_per_row');
    case 'classroom':         return p('rows') * p('seats_per_row');
    case 'u_shape':           return (p('tables_per_side') * 2 * p('seats_per_table')) + p('head_table_seats');
    case 'boardroom':         return p('total_seats');
    case 'banquet':           return p('tables') * p('seats_per_table');
    case 'cabaret':           return p('tables') * p('seats_per_table');
    case 'hollow_square':     return p('seats_per_side') * 4;
    case 'cocktail_reception': return (p('high_top_tables') * p('seats_per_high_top')) + p('standing_capacity');
    case 'cluster_pod':       return p('clusters') * p('seats_per_cluster');
    case 'chevron_v_shape':   return p('rows') * p('seats_per_row');
    case 'fishbone_herringbone': return p('tables') * p('seats_per_table');
    case 'lounge':            return p('seating_groups') * p('seats_per_group');
    case 'amphitheater':      return p('tiers') * p('seats_per_tier');
    case 'hybrid_virtual':    return p('in_person_seats') + p('virtual_stations');
    case 'eco_friendly_open_air': return p('seating_areas') * p('seats_per_area');
    case 'themed_custom_modular': return p('modules') * p('seats_per_module');
    default:                  return null;
  }
}

// Interfaces
export interface VenueRoom {
  id: number;
  venue_id: number;
  name: string;
  description?: string;
  room_type: RoomType;
  max_capacity?: number;
  floor_area_sqft?: number;
  has_av_equipment: boolean;
  has_projector: boolean;
  has_screen: boolean;
  has_whiteboard: boolean;
  has_video_conferencing: boolean;
  has_natural_light: boolean;
  has_stage: boolean;
  has_dance_floor: boolean;
  is_accessible: boolean;
  amenities?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  photos?: unknown[];
  sort_order: number;
  created_by: number;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
}

export interface RoomSeatingConfig {
  id: number;
  venue_room_id: number;
  config_type: SeatingConfigType;
  config_name: string;
  capacity?: number;
  notes?: string;
  default_params?: Record<string, number>;
  is_default: boolean;
  created_at: string;
}

export interface VenueRoomWithConfigs extends VenueRoom {
  seating_configs: RoomSeatingConfig[];
}

export interface CreateVenueRoomDto {
  name: string;
  description?: string;
  room_type?: RoomType;
  max_capacity?: number;
  floor_area_sqft?: number;
  has_av_equipment?: boolean;
  has_projector?: boolean;
  has_screen?: boolean;
  has_whiteboard?: boolean;
  has_video_conferencing?: boolean;
  has_natural_light?: boolean;
  has_stage?: boolean;
  has_dance_floor?: boolean;
  is_accessible?: boolean;
  amenities?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  sort_order?: number;
  seating_configs?: CreateSeatingConfigDto[];
}

export interface UpdateVenueRoomDto extends Partial<CreateVenueRoomDto> {}

export interface CreateSeatingConfigDto {
  config_type: SeatingConfigType;
  capacity?: number;
  notes?: string;
  default_params?: Record<string, number>;
  is_default?: boolean;
}

/**
 * Room data extracted from a venue website via AI.
 * Extends CreateVenueRoomDto with extraction metadata.
 * Includes backend-specific amenity fields that the AI returns.
 */
export interface ExtractedRoom extends CreateVenueRoomDto {
  /** AI confidence score 0-1 for this extraction */
  confidence?: number;
  /** The URL the room was extracted from */
  source_url?: string;
  /** Whether the user has selected this room for import (UI state) */
  selected?: boolean;
  // Backend-specific amenity fields returned by AI extraction
  has_outdoor_access?: boolean;
  has_kitchen_access?: boolean;
  has_wheelchair_access?: boolean;
  has_climate_control?: boolean;
  has_visual_aids?: boolean;
}
